import React, { useState } from "react";
import { X, Edit2 } from "lucide-react";
import { motion } from "framer-motion";
import { User } from "../../types/types";
import { usePostAuth } from "../../hooks/usePostAuth";
import {
  modalBackdropVariants,
  modalContentVariants,
} from "../../animations/animation";

interface UpdateUserModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
  onOpenPhotoModal: () => void;
  invalidateKey?: string[];
}

const UpdateUserModal: React.FC<UpdateUserModalProps> = ({
  user,
  onClose,
  onSuccess,
  onOpenPhotoModal,
  invalidateKey = ["get-alumnis"],
}) => {
  const [form, setForm] = useState({
    name: user.name ?? "",
    email: user.email ?? "",
    phone: user.phone ?? "",
    company: user.company ?? "",
    bio: user.bio ?? "",
    photo_url: user.photo_url ?? "",
    dob: user.dob ?? "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const { mutate: updateUser, isPending: loading } = usePostAuth(
    `/users/${user.id}`,
    { queryKeyToInvalidate: invalidateKey }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload: Partial<typeof form> & { _method?: "PATCH" } = {};
    if (form.name !== user.name) payload.name = form.name;
    if (form.email !== user.email) payload.email = form.email;
    if (form.phone !== user.phone) payload.phone = form.phone;
    if (form.company !== user.company) payload.company = form.company;
    if (form.bio !== user.bio) payload.bio = form.bio;
    if (form.photo_url !== user.photo_url) payload.photo_url = form.photo_url;
    if (form.dob !== user.dob) payload.dob = form.dob;

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    payload._method = "PATCH";

    updateUser(payload, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: () => {
        alert("Failed to update profile");
      },
    });
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      variants={modalBackdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        variants={modalContentVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6 relative">
          <h3 className="text-xl font-bold text-gray-900">Update Profile</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Header */}
          <div className="flex items-start gap-6 mb-6 border-b pb-6">
            <div className="flex-shrink-0 relative">
              <img
                src={
                  form.photo_url ||
                  user.photo_url ||
                  "https://via.placeholder.com/150"
                }
                alt={form.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
              />
              <button
                type="button"
                onClick={onOpenPhotoModal}
                className="absolute bottom-0 right-0 bg-orange-500 text-white rounded-full p-1 hover:bg-orange-600"
                title="Edit photo"
              >
                <Edit2 size={16} />
              </button>
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* Two-column layout for remaining fields */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                Personal Information
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Birthday
                  </label>
                  <input
                    name="dob"
                    type="date"
                    value={form.dob}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bio
                  </label>
                  <textarea
                    name="bio"
                    rows={3}
                    value={form.bio}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">
                Professional Information
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white rounded-md font-medium transition"
            >
              {loading ? "Saving..." : "Update"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default UpdateUserModal;
